import PropTypes from 'prop-types';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Button, Card, CardBody } from 'reactstrap';
import { Features, ReduxState } from 'types';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { activitiesAreEnabled, resumesAreEnabled } from 'utils/util/features';
import ElasticsearchAPI from '../../utils/api/ElasticsearchAPI';
import { loadOrRender } from '../../utils/util/formatter';
import ActivityCard from '../Activities/ActivityCard';
import ModalActivityEditorButton from '../Activities/ModalActivityEditorButton';
import EmptyState from '../Widgets/EmptyState';

const SkillActivities: FC<Props> = (props) => {
  const { formatMessage } = useIntl();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const [showCreateActivityDialog, setShowCreateActivityDialog] =
    useState(false);
  const [activities, setActivities] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(null);
  const { user } = useAuthProvider();
  const userSub = user?.sub;

  // @ts-expect-error
  const currentOrgId = props.currentOrganization?.id;

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    // fetch activities from ElasticSearch
    if (userSub && props.skillId && currentOrgId) {
      ElasticsearchAPI.getActivitiesForSkill(
        userSub,
        props.currentProxyPerson,
        currentOrgId,
        props.skillId,
        (newActivities) => {
          if (isMounted) {
            setActivities(newActivities);
          }
        },
        (message) => {
          setErrorMessage(message);
        }
      );
    }
  }, [
    isMounted,
    userSub,
    currentOrgId,
    props.skillId,
    props.currentProxyPerson,
  ]);

  const openCreateActivityDialog = useCallback(() => {
    setShowCreateActivityDialog(true);
  }, []);

  const onCreateActivityDialogClosed = useCallback(() => {
    setShowCreateActivityDialog(false);
  }, []);

  const loadOrRenderOutput = loadOrRender(activities, errorMessage);
  if (loadOrRenderOutput) {
    return loadOrRenderOutput;
  }

  const features = props.features as Features;
  const canCreateActivity =
    activitiesAreEnabled(features) && resumesAreEnabled(features);

  return (
    <>
      {/* @ts-expect-error */}
      {activities?.length === 0 && (
        <Card>
          <CardBody>
            <EmptyState
              title={formatMessage({
                id: 'app.views.skills.skill_activities.title.no_activities_found',
                defaultMessage: 'No activities found',
              })}
              subtitle={formatMessage({
                id: 'app.views.skills.skill_activities.subtitle.nobody_has_recorded_any_activities',
                defaultMessage:
                  'Nobody has recorded any activities using this skill.',
              })}
            >
              {canCreateActivity && (
                <Button color="primary" onClick={openCreateActivityDialog}>
                  <FormattedMessage
                    id="app.views.skills.skill_activities.empty_state.button.create_activity"
                    defaultMessage="Create activity"
                  />
                </Button>
              )}
            </EmptyState>
          </CardBody>
        </Card>
      )}
      <ModalActivityEditorButton
        isOpen={showCreateActivityDialog}
        onClosed={onCreateActivityDialogClosed}
        hideButton={true}
        buttonText={formatMessage({
          id: 'app.views.skills.skill_activities.modal.create_activity',
          defaultMessage: 'Create activity',
        })}
      />
      {/* @ts-expect-error */}
      {activities?.length > 0 &&
        // @ts-expect-error
        activities?.map((ac, acIndex) => (
          <ActivityCard
            key={acIndex}
            showPrompt={true}
            activity={ac}
            focalSkill={props.skill}
            setActivity={(a) =>
              // @ts-expect-error
              setActivities(activities.map((a2) => (a2.id === a.id ? a : a2)))
            }
            openCreateActivityDialog={openCreateActivityDialog}
          />
        ))}
    </>
  );
};

const SkillActivities_propTypes = {
  skillId: PropTypes.string.isRequired,
  skill: PropTypes.object,
  currentOrganization: PropTypes.object.isRequired,
  currentProxyPerson: PropTypes.object.isRequired,
  features: PropTypes.object,
};

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization, currentProxyPerson, features } = state;

  return {
    currentOrganization,
    currentProxyPerson,
    features,
  };
};

type Props = PropTypes.InferProps<typeof SkillActivities_propTypes>;

export default connect(mapStateToProps)(React.memo(SkillActivities));
